import { LESSON_PLAYER_ROUTES } from 'LessonPlayer/routes';
import { BROWSE_COURSES_ROUTES } from 'BrowseCourses/routes';
import { STREAM_DASHBOARD_ROUTES } from './routes';

type RouteDefinition = { path: string };

function createPathRegex({ path }: RouteDefinition): RegExp {
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    const pattern = normalizedPath.replace(/:[^/]+/g, '[^/]+');
    return new RegExp(`^${pattern}$`);
}

const STREAM_DASHBOARD_ROUTE_DEFINITIONS = Object.values(STREAM_DASHBOARD_ROUTES);
const STREAM_COMPLETED_ROUTE_DEFINITION = STREAM_DASHBOARD_ROUTES.STREAM_COMPLETED;
const STREAM_DASHBOARD_BREADCRUMB_DEFINITIONS = Object.entries(STREAM_DASHBOARD_ROUTES)
    .filter(([key]) => key !== 'STREAM_COMPLETED')
    .map(([, definition]) => definition);
const BROWSE_COURSES_ROUTE_DEFINITIONS = [BROWSE_COURSES_ROUTES.COURSES];

let lessonPlayerRouteRegexps: RegExp[] | null = null;
let streamDashboardRouteRegexps: RegExp[] | null = null;
let streamDashboardBreadcrumbRegexps: RegExp[] | null = null;
const streamCompletedRouteRegexp = createPathRegex(STREAM_COMPLETED_ROUTE_DEFINITION);
let browseCoursesRouteRegexps: RegExp[] | null = null;

function getLessonPlayerRouteRegexps(): RegExp[] {
    if (!lessonPlayerRouteRegexps) {
        lessonPlayerRouteRegexps = Object.values(LESSON_PLAYER_ROUTES).map(createPathRegex);
    }
    return lessonPlayerRouteRegexps;
}

function getStreamDashboardRouteRegexps(): RegExp[] {
    if (!streamDashboardRouteRegexps) {
        streamDashboardRouteRegexps = STREAM_DASHBOARD_ROUTE_DEFINITIONS.map(createPathRegex);
    }
    return streamDashboardRouteRegexps;
}

function getStreamDashboardBreadcrumbRegexps(): RegExp[] {
    if (!streamDashboardBreadcrumbRegexps) {
        streamDashboardBreadcrumbRegexps = STREAM_DASHBOARD_BREADCRUMB_DEFINITIONS.map(createPathRegex);
    }
    return streamDashboardBreadcrumbRegexps;
}

// /courses
function getBrowseCoursesRouteRegexps(): RegExp[] {
    if (!browseCoursesRouteRegexps) {
        browseCoursesRouteRegexps = BROWSE_COURSES_ROUTE_DEFINITIONS.map(createPathRegex);
    }
    return browseCoursesRouteRegexps;
}

function matchesAny(path: string, regexps: RegExp[]): boolean {
    return regexps.some(regex => regex.test(path));
}

function isLessonPlayerPath(path: string): boolean {
    return matchesAny(path, getLessonPlayerRouteRegexps());
}

function isStreamDashboardPath(path: string): boolean {
    return matchesAny(path, getStreamDashboardRouteRegexps());
}

function isStreamDashboardBreadcrumbPath(path: string): boolean {
    return matchesAny(path, getStreamDashboardBreadcrumbRegexps());
}

function isBrowseCoursesPath(path: string): boolean {
    return matchesAny(path, getBrowseCoursesRouteRegexps());
}

function isStreamCompletedPath(path: string): boolean {
    return streamCompletedRouteRegexp.test(path);
}

// As long as we are still in the learning section, we keep tracking breadcrumbs through
// the section
export function isLearningSectionPath(path: string): boolean {
    if (path.startsWith('/dashboard')) {
        return true;
    }
    if (isBrowseCoursesPath(path)) {
        return true;
    }
    if (isStreamDashboardPath(path)) {
        return true;
    }
    if (isLessonPlayerPath(path)) {
        return true;
    }
    return false;
}

// When we are navigating back through breadcrumbs, we only navigate to stream dashboards,
// /courses, or the student dashboard (not the lesson player or stream completed pages)
export function isBreadcrumbBackNavTargetPath(path: string): boolean {
    if (path.startsWith('/dashboard')) {
        return true;
    }
    if (isBrowseCoursesPath(path)) {
        return true;
    }
    if (isStreamCompletedPath(path)) {
        return false;
    }
    return isStreamDashboardBreadcrumbPath(path);
}
